import '@fortawesome/fontawesome-free/js/all';
import * as Sentry from '@sentry/browser';
import i18next, { ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';
import React, { StrictMode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import YAML from 'yaml';
import App from './components/App';
import { I18NEXT_DEBUG, I18NEXT_URL } from './config';
import './style.scss';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CookiesProvider } from 'react-cookie';
import { Settings } from 'luxon';
import { registerModulePreloadRecovery } from './util/modulePreload';

import enCommon from '../../locales/en/common.yaml';
import deCommon from '../../locales/de/common.yaml';
import { Language } from '../../shared/utils/enums';
import { defaultLanguage, supportedLanguages } from '../../shared/consts';

const resources: { [language in Language]: ResourceLanguage } = {
  [Language.GERMAN]: { common: deCommon },
  [Language.ENGLISH]: { common: enCommon },
};

function applyLanguage(lng?: string): void {
  if (!lng) {
    return;
  }

  Settings.defaultLocale = lng;
  document.documentElement.lang = lng;
}

function initSentry(): void {
  try {
    const env: ImportMetaEnv = import.meta.env ?? process.env;

    if (env?.VITE_ENVIRONMENT && env.VITE_ENVIRONMENT !== 'development') {
      Sentry.init({
        dsn: env.VITE_SENTRY_DSN,
        environment: env.VITE_ENVIRONMENT,
      });
      Sentry.setUser(null);
    }
  } catch (error) {
    console.error(error);
  }
}

initSentry();
registerModulePreloadRecovery();


i18next
  .use(initReactI18next)
  .init(
    {
      resources,
      load: 'languageOnly',
      fallbackLng: defaultLanguage,
      supportedLngs: supportedLanguages,
      preload: supportedLanguages,
      nonExplicitSupportedLngs: true,
      debug: Boolean(I18NEXT_DEBUG),
      ns: ['common'],
      fallbackNS: 'common',
      backend: {
        loadPath: I18NEXT_URL,
        parse: YAML.parse,
      },
      saveMissing: true,
      missingKeyHandler: console.log,
    },
    () => {
      applyLanguage(i18next.language);

      i18next.on('languageChanged', (lng: string) => {
        applyLanguage(lng);
      });


      const rootElement: HTMLElement | null = document.getElementById('root');
      if (!rootElement) {
        console.error('Root element not found');
        return;
      }

      const root: Root = createRoot(rootElement);

      root.render(
        <StrictMode>
          <DndProvider backend={HTML5Backend}>
            <CookiesProvider>
              <App />
            </CookiesProvider>
          </DndProvider>
        </StrictMode>
      );
    }
  );
